import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, CheckCircle2, History, X } from "lucide-react";
import { CONTENT_REQUEST_TYPES, contentTypeLabel, CONTENT_REQUEST_STATUSES, contentRequestStatus, ITEM_STATE_META, frDate } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const PRIORITIES = [["haute", "Haute"], ["normale", "Normale"], ["basse", "Basse"]];
const sel = "mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white";

const EMPTY_ITEM = { type: "photo", title: "", quantity: "", description: "" };

function CreateRequestModal({ open, setOpen, client, pubs, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", deadline: "", priority: "normale", publication_id: "", campaign: "", comment: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k) => (e) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));

  const submit = async () => {
    const validItems = items.filter((i) => i.title.trim());
    if (!form.title.trim() || !form.deadline || !validItems.length) {
      toast.error("Titre, deadline et au moins un contenu demandé sont requis");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/clients/${client.id}/content-requests`, {
        ...form,
        publication_id: form.publication_id || null,
        items: validItems.map((i) => ({ ...i, title: i.title.trim(), quantity: i.quantity ? Number(i.quantity) : null })),
      });
      toast.success("Demande créée — le client est notifié");
      setOpen(false);
      setForm({ title: "", description: "", deadline: "", priority: "normale", publication_id: "", campaign: "", comment: "" });
      setItems([{ ...EMPTY_ITEM }]);
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de la création"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cr-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Demander du contenu</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input data-testid="cr-title" className="mt-1.5" value={form.title} onChange={set("title")} placeholder="Ex : Shooting collection septembre" /></div>
          <div><Label>Description</Label><textarea data-testid="cr-description" className={sel} rows={2} value={form.description} onChange={set("description")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Deadline</Label><Input data-testid="cr-deadline" type="date" className="mt-1.5" value={form.deadline} onChange={set("deadline")} /></div>
            <div><Label>Priorité</Label>
              <select data-testid="cr-priority" className={sel} value={form.priority} onChange={set("priority")}>
                {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label>Publication concernée <span className="text-neutral-400 font-normal">(optionnel)</span></Label>
            <select data-testid="cr-publication" className={sel} value={form.publication_id} onChange={set("publication_id")}>
              <option value="">Aucune</option>
              {pubs.map((p) => <option key={p.id} value={p.id}>{p.title} — {frDate(p.date)}</option>)}
            </select>
          </div>
          <div><Label>Projet / campagne <span className="text-neutral-400 font-normal">(optionnel)</span></Label><Input data-testid="cr-campaign" className="mt-1.5" value={form.campaign} onChange={set("campaign")} /></div>

          <div>
            <Label>Contenus demandés</Label>
            <div className="space-y-2 mt-1.5">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2 items-start border border-neutral-200 rounded-lg p-2.5" data-testid={`cr-item-${i}`}>
                  <select className="text-sm border border-neutral-200 rounded-md px-2 py-1.5 bg-white" value={it.type} onChange={setItem(i, "type")} data-testid={`cr-item-type-${i}`}>
                    {CONTENT_REQUEST_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <Input className="flex-1" placeholder="Ex : 5 photos produits" value={it.title} onChange={setItem(i, "title")} data-testid={`cr-item-title-${i}`} />
                  <Input className="w-20" type="number" min="1" placeholder="Qté" value={it.quantity} onChange={setItem(i, "quantity")} data-testid={`cr-item-qty-${i}`} />
                  {items.length > 1 && (
                    <button className="p-1.5 text-neutral-400 hover:text-red-600" onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))} data-testid={`cr-item-remove-${i}`}><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="cr-add-item" onClick={() => setItems((a) => [...a, { ...EMPTY_ITEM }])} className="gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Ajouter un élément
              </Button>
            </div>
          </div>
          <div><Label>Commentaire <span className="text-neutral-400 font-normal">(optionnel)</span></Label><textarea data-testid="cr-comment" className={sel} rows={2} value={form.comment} onChange={set("comment")} /></div>
          <Button data-testid="cr-submit" onClick={submit} disabled={loading} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {loading ? "Création…" : "Créer la demande"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ContentRequests({ client, pubs }) {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(false);
  const [history, setHistory] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get(`/clients/${client.id}/content-requests`).then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, [client.id]);

  const validate = async (r) => {
    await api.patch(`/content-requests/${r.id}`, { action: "valider" });
    toast.success("Contenus validés");
    load();
  };
  const remove = async (r) => {
    await api.delete(`/content-requests/${r.id}`);
    toast.success("Demande supprimée");
    load();
  };
  const openHistory = async (r) => {
    const { data } = await api.get(`/content-requests/${r.id}/history`);
    setHistory({ request: r, rows: data });
  };

  const shown = filter ? requests.filter((r) => r.status === filter) : requests;

  return (
    <div className="space-y-4" data-testid="cr-section">
      <div className="flex flex-wrap items-center gap-2">
        {[["", "Tous"], ...CONTENT_REQUEST_STATUSES.map((s) => [s.id, s.label])].map(([v, l]) => (
          <button key={v} data-testid={`cr-filter-${v || "tous"}`} onClick={() => setFilter(v)}
            className={`text-sm rounded-md px-3 py-1.5 transition-colors ${filter === v ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}>
            {l}
          </button>
        ))}
        <Button data-testid="cr-new-btn" onClick={() => setModal(true)} className="ml-auto bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
          <Plus className="w-4 h-4" /> Demander du contenu
        </Button>
      </div>

      {shown.length === 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center text-sm text-neutral-400" data-testid="cr-empty">
          Aucune demande de contenu. Créez-en une pour que le client dépose ses fichiers directement dans son portail.
        </div>
      )}

      {shown.map((r) => {
        const st = contentRequestStatus(r.status);
        const pub = r.publication_id ? pubs.find((p) => p.id === r.publication_id) : null;
        return (
          <div key={r.id} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5" data-testid={`cr-card-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading font-bold flex-1 min-w-0">{r.title}</h3>
              {r.priority === "haute" && <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Priorité haute</span>}
              <span className={`text-xs font-medium border rounded-full px-2.5 py-1 ${st.cls}`} data-testid={`cr-status-${r.id}`}>{st.label}</span>
            </div>
            <p className="text-xs text-neutral-400 mt-1">
              Deadline : {frDate(r.deadline)}
              {r.campaign ? ` · Campagne : ${r.campaign}` : ""}
              {pub ? ` · Publication : « ${pub.title} » (${frDate(pub.date)})` : ""}
            </p>
            {pub && (
              <button className="text-xs text-blue-600 hover:underline mt-1" data-testid={`cr-goto-pub-${r.id}`}
                onClick={() => navigate(`/publications/${pub.id}`)}>
                Voir la publication →
              </button>
            )}
            {r.description && <p className="text-sm text-neutral-600 mt-2">{r.description}</p>}

            <p className="text-sm font-medium mt-3" data-testid={`cr-progress-${r.id}`}>
              {r.received_count} / {r.items_count} éléments reçus
            </p>
            <div className="mt-2 space-y-1.5">
              {(r.items || []).map((it) => {
                const m = ITEM_STATE_META[it.state] || ITEM_STATE_META.en_attente;
                return (
                  <div key={it.id} className="flex items-start gap-2 text-sm" data-testid={`cr-item-state-${it.id}`}>
                    <span>{m.icon}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{it.title}</span>
                      <span className="text-neutral-400"> · {contentTypeLabel(it.type)}{it.quantity ? ` · ×${it.quantity}` : ""}</span>
                      {(it.files?.length > 0 || it.text || it.links?.length > 0) && (
                        <p className="text-xs text-neutral-400">
                          {it.files?.length > 0 && `${it.files.length} fichier(s)`}
                          {it.text && " · texte reçu"}
                          {it.links?.length > 0 && ` · ${it.links.length} lien(s)`}
                          {it.received_at && ` · reçu le ${format(parseISO(it.received_at), "d MMM 'à' HH:mm", { locale: fr })}`}
                        </p>
                      )}
                      {it.files?.length > 0 && (
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          {it.files.map((f, k) => (
                            <a key={k} href={f.url} target="_blank" rel="noreferrer"
                              className="text-[10px] bg-neutral-100 hover:bg-neutral-200 rounded px-1.5 py-0.5 transition-colors">{f.name}</a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {(r.comments || []).length > 0 && (
              <div className="mt-3 bg-neutral-50 rounded-lg p-3 space-y-1">
                {r.comments.map((c) => (
                  <p key={c.id} className="text-xs text-neutral-600"><strong>{c.author}</strong> — {c.text}</p>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              {r.status !== "valide" && (
                <Button size="sm" data-testid={`cr-validate-${r.id}`} onClick={() => validate(r)}
                  className="bg-green-600 hover:bg-green-700 text-white gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Valider les contenus
                </Button>
              )}
              <Button size="sm" variant="outline" data-testid={`cr-history-${r.id}`} onClick={() => openHistory(r)} className="gap-1.5">
                <History className="w-3.5 h-3.5" /> Historique
              </Button>
              <Button size="sm" variant="outline" data-testid={`cr-delete-${r.id}`} onClick={() => remove(r)}
                className="text-red-600 hover:bg-red-50 gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </Button>
            </div>
          </div>
        );
      })}

      <CreateRequestModal open={modal} setOpen={setModal} client={client} pubs={pubs} onCreated={load} />

      <Dialog open={!!history} onOpenChange={() => setHistory(null)}>
        <DialogContent className="max-w-md" data-testid="cr-history-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Historique — {history?.request?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(history?.rows || []).map((h) => (
              <div key={h.id} className="text-sm flex gap-2">
                <span className="text-neutral-400 shrink-0 text-xs mt-0.5">{format(parseISO(h.created_at), "d MMM HH:mm", { locale: fr })}</span>
                <span><strong>{h.actor}</strong> — {h.action}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
